import { useEffect, useState } from "react";
import { Search, GitBranch, Factory, Beaker, UserCheck, X, ShieldCheck, Truck, AlertTriangle, ShoppingCart, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { formatDateEU, formatEuro } from "@/lib/constants";

const STATUS_COLORS = {
  Planned: "#F4A261",
  "In Production": "#E85D04",
  Completed: "#1F4D3A",
  "Sample Use": "#7B6DCC",
  "Commercial Use": "#2A9D8F",
  Archived: "#6C757D",
};

const OUTCOME_COLORS = {
  "Pending Feedback": "#F4A261",
  "Positive Feedback": "#2A9D8F",
  "Needs Adjustment": "#E76F51",
  "Interested": "#E85D04",
  "Ordered": "#2A9D8F",
  "Not Interested": "#6C757D",
};

export default function Traceability() {
  const [filters, setFilters] = useState({
    q: "", batch_id: "", restaurant: "", customer: "", date: "", product: "",
  });
  const [results, setResults] = useState({ production_batches: [], samples: [], customers: [], hygiene_checks: [], raw_material_checks: [], incidents: [], orders: [], waste_records: [] });
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async (payload = filters) => {
    setLoading(true);
    try {
      // Drop empty values to keep request clean
      const clean = Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== "" && v != null));
      const data = await api.traceability(clean);
      setResults(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.getMeta().then(setMeta);
    fetchData(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = (e) => {
    e?.preventDefault?.();
    fetchData();
  };
  const clearAll = () => {
    const reset = { q: "", batch_id: "", restaurant: "", customer: "", date: "", product: "" };
    setFilters(reset);
    fetchData(reset);
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const totalCount =
    results.production_batches.length + results.samples.length + results.customers.length
    + (results.hygiene_checks?.length || 0)
    + (results.raw_material_checks?.length || 0)
    + (results.incidents?.length || 0)
    + (results.orders?.length || 0)
    + (results.waste_records?.length || 0);

  return (
    <div data-testid="page-traceability">
      <PageHeader
        overline="Food Traceability"
        title="Traceability"
        subtitle="From production batch → sample delivery → customer order. Search by batch, restaurant, customer, date or product."
      />

      <form
        onSubmit={apply}
        className="card-soft p-5 mb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
        data-testid="trace-form"
      >
        <Field label="Free Search">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" />
            <Input
              value={filters.q}
              onChange={(e) => set("q", e.target.value)}
              placeholder="Anything…"
              className="pl-9 bg-white border-[#E5E5E5]"
              data-testid="trace-q"
            />
          </div>
        </Field>
        <Field label="Batch ID">
          <Input value={filters.batch_id} onChange={(e) => set("batch_id", e.target.value)}
            placeholder="VH-20260715-01" className="font-mono" data-testid="trace-batch-id" />
        </Field>
        <Field label="Restaurant">
          <Input value={filters.restaurant} onChange={(e) => set("restaurant", e.target.value)}
            placeholder="e.g. Tacoya" data-testid="trace-restaurant" />
        </Field>
        <Field label="Customer">
          <Input value={filters.customer} onChange={(e) => set("customer", e.target.value)}
            placeholder="e.g. Taqueria Norte" data-testid="trace-customer" />
        </Field>
        <Field label="Date">
          <Input type="date" value={filters.date} onChange={(e) => set("date", e.target.value)}
            data-testid="trace-date" />
        </Field>
        <Field label="Product">
          <Select value={filters.product || "__any"} onValueChange={(v) => set("product", v === "__any" ? "" : v)}>
            <SelectTrigger className="bg-white border-[#E5E5E5]" data-testid="trace-product">
              <SelectValue placeholder="Any product" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__any">Any product</SelectItem>
              {meta?.products?.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
            </SelectContent>
          </Select>
        </Field>
        <div className="sm:col-span-2 lg:col-span-3 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={clearAll} data-testid="trace-clear">
            <X size={14} className="mr-2" /> Clear
          </Button>
          <Button type="submit" className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="trace-search">
            <Search size={14} className="mr-2" /> Search
          </Button>
        </div>
      </form>

      <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#1F4D3A] mb-3" data-testid="trace-results-count">
        {loading ? "Searching…" : `${totalCount} result${totalCount === 1 ? "" : "s"}`}
      </div>

      {/* Production batches */}
      <Section icon={Factory} title="Production Batches" count={results.production_batches.length} testId="trace-batches-section">
        {results.production_batches.map((b) => (
          <div key={b.id} className="card-soft p-4" data-testid={`trace-batch-${b.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-mono text-[#E85D04] font-bold">{b.batch_id}</div>
                <div className="text-xs text-[#6C757D]">{b.product} · {formatDateEU(b.production_date)}</div>
              </div>
              <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                style={{ backgroundColor: `${STATUS_COLORS[b.production_status] || "#6C757D"}1f`, color: STATUS_COLORS[b.production_status] || "#6C757D" }}>
                {b.production_status}
              </span>
            </div>
            <div className="mt-2 text-sm text-[#222]">
              <span className="text-[#6C757D] text-xs uppercase tracking-wider mr-2">Liters</span>{b.liters_produced} L
              <span className="text-[#6C757D] text-xs uppercase tracking-wider mx-2">pH</span>{b.ph ?? "—"}
            </div>
            {b.production_notes && <div className="mt-1 text-xs text-[#222]/80 line-clamp-2">{b.production_notes}</div>}
          </div>
        ))}
      </Section>

      {/* Samples */}
      <Section icon={Beaker} title="Sample Deliveries" count={results.samples.length} testId="trace-samples-section">
        {results.samples.map((s) => (
          <div key={s.id} className="card-soft p-4" data-testid={`trace-sample-${s.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-[#222] truncate">{s.company_name}</div>
                <div className="text-xs text-[#6C757D]">{s.contact_person || "—"} · {formatDateEU(s.date_delivered)}</div>
              </div>
              <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider"
                style={{ backgroundColor: `${OUTCOME_COLORS[s.outcome] || "#6C757D"}1f`, color: OUTCOME_COLORS[s.outcome] || "#6C757D" }}>
                {s.outcome}
              </span>
            </div>
            {s.batches?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {s.batches.map((b, i) => (
                  <span key={i} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#F7F1E8] text-[#1F4D3A] font-semibold border border-[#E5E5E5]">
                    {b.batch_number}
                  </span>
                ))}
              </div>
            )}
            {s.feedback && <div className="mt-2 text-sm text-[#222]"><span className="text-[#2A9D8F] text-xs uppercase tracking-wider mr-1">Feedback:</span>{s.feedback}</div>}
          </div>
        ))}
      </Section>

      {/* Customers */}
      <Section icon={UserCheck} title="Customer Deliveries" count={results.customers.length} testId="trace-customers-section">
        {results.customers.map((c) => (
          <div key={c.id} className="card-soft p-4" data-testid={`trace-customer-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-[#222]">{c.customer_name}</div>
                <div className="text-xs text-[#6C757D]">{c.city || "—"} · since {formatDateEU(c.first_order_date)}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Actual /mo</div>
                <div className="font-semibold text-[#2A9D8F]">{formatEuro(c.actual_monthly_revenue)}</div>
              </div>
            </div>
            {c.purchased_batch_numbers?.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {c.purchased_batch_numbers.map((bn) => (
                  <span key={bn} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#F7F1E8] text-[#1F4D3A] font-semibold border border-[#E5E5E5]">
                    {bn}
                  </span>
                ))}
              </div>
            )}
            {c.last_batch_delivered && (
              <div className="mt-1 text-xs text-[#6C757D]">
                Last batch: <span className="font-mono text-[#222]">{c.last_batch_delivered}</span>
              </div>
            )}
          </div>
        ))}
      </Section>

      {/* Raw Materials — backward traceability */}
      <Section icon={Truck} title="Raw Materials" count={results.raw_material_checks?.length || 0} testId="trace-raw-materials-section">
        {(results.raw_material_checks || []).map((r) => (
          <div key={r.id} className="card-soft p-4" data-testid={`trace-raw-material-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-[#222]">{r.ingredient_name || r.product || "—"}</div>
                <div className="text-xs text-[#6C757D]">{r.supplier || "—"} · {formatDateEU(r.check_date)}</div>
              </div>
              <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-[#F7F1E8] text-[#1F4D3A] font-semibold border border-[#E5E5E5]">
                {r.linked_batch_id || r.batch_id || "—"}
              </span>
            </div>
            <div className="mt-2 text-xs text-[#222]/80 flex gap-3">
              {r.temperature_c != null && <span><span className="text-[#6C757D] uppercase tracking-wider mr-1">Temp</span>{r.temperature_c}°C</span>}
              {r.temperature_status && <span className={r.temperature_status === "OK" ? "text-[#1F4D3A]" : "text-[#D62828]"}>· {r.temperature_status}</span>}
              {r.packaging_condition && <span>· Pack: {r.packaging_condition}</span>}
            </div>
          </div>
        ))}
      </Section>

      {/* Hygiene checks — backward traceability */}
      <Section icon={ShieldCheck} title="Hygiene Checks" count={results.hygiene_checks?.length || 0} testId="trace-hygiene-section">
        {(results.hygiene_checks || []).map((h) => (
          <div key={h.id} className="card-soft p-4" data-testid={`trace-hygiene-${h.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-[#222]">{formatDateEU(h.check_date)} · {h.check_time || ""}</div>
                <div className="text-xs text-[#6C757D]">By {h.user || h.checked_by || h.created_by || "—"}</div>
              </div>
              <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-[#F7F1E8] text-[#1F4D3A] font-semibold border border-[#E5E5E5]">
                {h.linked_batch_id || h.batch_id || "—"}
              </span>
            </div>
            {h.workspace_condition && (
              <div className={`mt-2 text-xs font-semibold ${h.workspace_condition === "OK" ? "text-[#1F4D3A]" : "text-[#D62828]"}`}>
                Workspace: {h.workspace_condition}
              </div>
            )}
            {h.notes && <div className="mt-1 text-xs text-[#222]/80 line-clamp-2">{h.notes}</div>}
          </div>
        ))}
      </Section>

      {/* Incidents — forward traceability */}
      <Section icon={AlertTriangle} title="Incidents" count={results.incidents?.length || 0} testId="trace-incidents-section">
        {(results.incidents || []).map((i) => (
          <div key={i.id} className="card-soft p-4" data-testid={`trace-incident-${i.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-[#222] truncate">{i.incident_type || i.description || "Incident"}</div>
                <div className="text-xs text-[#6C757D]">{formatDateEU(i.incident_date)} · by {i.reported_by || i.created_by || "—"}</div>
              </div>
              <div className="flex gap-1 items-start flex-wrap justify-end">
                {i.severity && (
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                    style={{
                      backgroundColor: i.severity === "High" ? "#D6282820" : i.severity === "Medium" ? "#E85D0420" : "#1F4D3A20",
                      color: i.severity === "High" ? "#D62828" : i.severity === "Medium" ? "#E85D04" : "#1F4D3A",
                    }}
                  >
                    {i.severity}
                  </span>
                )}
                <span className="font-mono text-[10px] px-2 py-0.5 rounded-full bg-[#F7F1E8] text-[#1F4D3A] font-semibold border border-[#E5E5E5]">
                  {i.linked_batch_id || i.batch_id || "—"}
                </span>
              </div>
            </div>
            {(i.description || i.issue) && <div className="mt-2 text-xs text-[#222]/80 line-clamp-2">{i.description || i.issue}</div>}
          </div>
        ))}
      </Section>

      {/* Orders — forward traceability */}
      <Section icon={ShoppingCart} title="Orders" count={results.orders?.length || 0} testId="trace-orders-section">
        {(results.orders || []).map((o) => (
          <div key={o.id} className="card-soft p-4" data-testid={`trace-order-${o.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-[#222]">{o.customer_name || "—"}</div>
                <div className="text-xs text-[#6C757D]">{formatDateEU(o.order_date)} · {o.items?.length || 0} item{(o.items?.length || 0) === 1 ? "" : "s"}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">Subtotal</div>
                <div className="font-semibold text-[#2A9D8F]">{formatEuro(o.subtotal || 0)}</div>
              </div>
            </div>
            {o.recalled && (
              <div className="mt-2 text-[10px] font-bold uppercase tracking-wider text-[#D62828]">RECALLED</div>
            )}
          </div>
        ))}
      </Section>

      {/* Waste — forward traceability */}
      <Section icon={Trash2} title="Waste" count={results.waste_records?.length || 0} testId="trace-waste-section">
        {(results.waste_records || []).map((w) => (
          <div key={w.id} className="card-soft p-4" data-testid={`trace-waste-${w.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-[#222] truncate">{w.product_name || "—"}</div>
                <div className="text-xs text-[#6C757D]">{formatDateEU(w.waste_date)} · by {w.recorded_by || "—"}</div>
              </div>
              <div className="text-right">
                <div className="font-bold text-[#E76F51]">−{w.quantity} <span className="text-[10px] text-[#6C757D] font-normal">{w.unit || "units"}</span></div>
                <span className="mt-1 inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#E76F51]/15 text-[#E76F51]">{w.reason || "—"}</span>
              </div>
            </div>
          </div>
        ))}
      </Section>

      {totalCount === 0 && !loading && (
        <div className="card-soft p-10 text-center" data-testid="trace-empty">
          <GitBranch size={28} className="mx-auto text-[#E85D04] mb-2" />
          <div className="font-display font-semibold text-[#222]">No matches</div>
          <div className="text-sm text-[#6C757D] mt-1">Try a different batch ID, restaurant or product.</div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">{label}</Label>
    {children}
  </div>
);

const Section = ({ icon: Icon, title, count, testId, children }) => {
  if (count === 0) return null;
  return (
    <div className="mb-6" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className="text-[#E85D04]" />
        <div className="font-display font-semibold text-lg text-[#222]">{title}</div>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FDEBDD] text-[#E85D04] font-bold">{count}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">{children}</div>
    </div>
  );
};
